import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { isMobile } from '../utils/platform';

const PCC_WEBSITE = 'https://www.pariscentrechretien.com';

/**
 * VisitorRoute — public visitor browsing (accueil, événements, dons, prière,
 * RDV visiteur…).
 *
 * ACCESS POLICY (decision Louis, 2026-07): the WEB version is reserved for
 * account holders. On a desktop-width browser (`platform === 'web'`), an
 * unauthenticated user is redirected to /login. On phone-width (the native
 * mobile app or a mobile browser) the public visitor experience is kept —
 * that's the evangelism funnel (prayer / RDV that converts into an account).
 *
 * Always-public pages (login, register, activate, reset, /supprimer-mon-compte,
 * CMS /page/:slug) are separate routes NOT wrapped by VisitorRoute, so they
 * stay reachable on desktop too.
 */
export const VisitorRoute = ({ children }) => {
  const { isAuthenticated, platform, loading } = useAuth();
  const location = useLocation();

  // Wait for the session check to resolve before deciding — otherwise an
  // authenticated desktop member could be bounced to /login on first paint.
  if (loading) return null;

  if (platform === 'web' && !isAuthenticated) {
    return <Navigate to="/login" replace state={{ from: location }} />;
  }

  return children;
};

/**
 * MobileOnlyRoute - Strictly mobile only. Shows a message on desktop.
 */
export const MobileOnlyRoute = ({ children }) => {
  const { platform } = useAuth();

  if (platform === 'web' && !isMobile()) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-pcc-background">
        <div className="text-center px-4 max-w-md">
          <div className="w-16 h-16 mx-auto mb-6 bg-pcc-primary/10 rounded-full flex items-center justify-center">
            <svg className="w-8 h-8 text-pcc-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" />
            </svg>
          </div>
          <h2 className="text-xl font-bold text-pcc-text mb-2">Application Mobile</h2>
          <p className="text-pcc-text-muted mb-6">
            Cette section est optimisée pour les appareils mobiles.
            Veuillez accéder à cette page depuis votre smartphone.
          </p>
          <a
            href={PCC_WEBSITE}
            className="inline-block px-6 py-3 bg-pcc-primary text-white font-medium rounded-xl hover:bg-pcc-primary-dark transition-colors"
          >
            Visiter notre site web
          </a>
        </div>
      </div>
    );
  }

  return children;
};

export default VisitorRoute;
